import React from "react";
import {
  Box,
  Stack,
  Typography,
  Switch,
  Select,
  MenuItem,
  Chip,
  Divider,
  Alert,
  Link,
} from "@mui/material";
import { OpenInNew, WarningAmber } from "@mui/icons-material";
import {
  ColorsConstants,
  dashboardCardGreenTextColor,
  grayFontColor,
} from "../../../../cssUtils/constants";
import typography from "../../../../cssUtils/typography";
import { StringField } from "../ConfigFields/StringField";
import { formatTitle } from "../utils";

const PLATFORM_LABELS = {
  woocommerce: "WooCommerce",
  shopify: "Shopify",
  magento: "Magento",
};

const SUPPORTED_PLATFORMS = ["woocommerce", "shopify", "magento"];

const MANAGE_INTEGRATIONS_VALUE = "__manage_integrations__";
const INTEGRATIONS_PAGE_KEY = "carepms.all";

const PlatformStatusChip = ({ connected }) => (
  <Chip
    label={connected ? "Connected" : "Not connected"}
    size="small"
    sx={{
      backgroundColor: connected ? "#E6F4EA" : ColorsConstants.secondaryGray,
      color: connected ? dashboardCardGreenTextColor : grayFontColor,
      fontWeight: 600,
      height: 22,
    }}
  />
);

const OrderConfirmationPreset = ({
  schema,
  config,
  handleConfigChange,
  enabledEcommercePlatforms,
  ecommercePlatformDetails,
}) => {
  const connectedPlatforms = enabledEcommercePlatforms || [];
  const platformDetails = ecommercePlatformDetails || {};
  const isPushEnabled = config["push_order_on_payment_success"] ?? true;
  const hasConnectedPlatform = connectedPlatforms.length > 0;
  const selectedPlatform = config["push_platform"] || "";

  const goToIntegrations = () => {
    localStorage.setItem("activePage", INTEGRATIONS_PAGE_KEY);
    window.location.reload();
  };

  const handlePlatformChange = (event) => {
    const value = event.target.value;

    if (value === MANAGE_INTEGRATIONS_VALUE) {
      goToIntegrations();
      return;
    }

    handleConfigChange("push_platform", value);
  };

  return (
    <Stack spacing={3}>
      <Typography sx={{ ...typography.bodyLarge, fontWeight: 600 }}>
        Configuration:
      </Typography>

      <StringField
        fieldKey="confirmation_message"
        schema={schema?.properties?.["confirmation_message"] || {}}
        value={config["confirmation_message"]}
        required={false}
        onChange={(val) => handleConfigChange("confirmation_message", val)}
      />

      <Divider />

      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography
          sx={{
            ...typography.bodySmall,
            fontWeight: 600,
            textTransform: "uppercase",
            color: grayFontColor,
          }}
        >
          Push Order on Payment Success
        </Typography>
        <Switch
          checked={isPushEnabled}
          onChange={(e) =>
            handleConfigChange("push_order_on_payment_success", e.target.checked)
          }
          sx={{
            "& .MuiSwitch-switchBase.Mui-checked": {
              color: ColorsConstants.primaryBlue100,
            },
            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
              backgroundColor: ColorsConstants.primaryBlue100,
            },
          }}
        />
      </Stack>

      {isPushEnabled && (
        <Box>
          <Typography
            sx={{
              ...typography.bodySmall,
              fontWeight: 600,
              textTransform: "uppercase",
              color: grayFontColor,
              mb: 1,
            }}
          >
            Send Order To
          </Typography>

          <Select
            fullWidth
            displayEmpty
            value={selectedPlatform}
            onChange={handlePlatformChange}
            renderValue={(selected) => {
              if (!selected) {
                return (
                  <Typography
                    sx={{
                      ...typography.bodyLarge,
                      color: ColorsConstants.primaryGray40,
                    }}
                  >
                    Select a platform
                  </Typography>
                );
              }

              const connected = connectedPlatforms.includes(selected);

              return (
                <Stack direction="row" alignItems="center" gap={1}>
                  <Typography sx={typography.bodyLarge}>
                    {PLATFORM_LABELS[selected] || formatTitle(selected)}
                  </Typography>
                  <PlatformStatusChip connected={connected} />
                </Stack>
              );
            }}
            sx={{ "& .MuiSelect-select": { py: 1.2 } }}
          >
            {SUPPORTED_PLATFORMS.map((platformKey) => {
              const connected = connectedPlatforms.includes(platformKey);
              const siteUrl = connected ? platformDetails[platformKey]?.siteUrl : null;

              return (
                <MenuItem key={platformKey} value={platformKey} disabled={!connected}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ width: "100%" }}
                  >
                    <Box>
                      <Typography sx={typography.bodyLarge}>
                        {PLATFORM_LABELS[platformKey]}
                      </Typography>
                      {siteUrl && (
                        <Typography
                          sx={{ ...typography.bodySmall, color: grayFontColor }}
                        >
                          {siteUrl}
                        </Typography>
                      )}
                    </Box>
                    <PlatformStatusChip connected={connected} />
                  </Stack>
                </MenuItem>
              );
            })}

            <Divider />

            <MenuItem value={MANAGE_INTEGRATIONS_VALUE}>
              <Stack direction="row" alignItems="center" gap={0.5}>
                <OpenInNew
                  sx={{
                    fontSize: 16,
                    color: ColorsConstants.secondaryBackgroundColor,
                  }}
                />
                <Typography
                  sx={{
                    ...typography.bodyLarge,
                    color: ColorsConstants.secondaryBackgroundColor,
                  }}
                >
                  Manage platforms in Integrations
                </Typography>
              </Stack>
            </MenuItem>
          </Select>

          {!hasConnectedPlatform && (
            <Alert
              severity="warning"
              icon={<WarningAmber fontSize="inherit" />}
              sx={{ mt: 1.5, "& .MuiAlert-message": typography.bodySmall }}
            >
              No e-commerce platform is connected yet.{" "}
              <Link
                component="button"
                type="button"
                onClick={goToIntegrations}
                sx={{ ...typography.bodySmall, fontWeight: 600 }}
              >
                Connect one in Integrations
              </Link>{" "}
              to push orders automatically.
            </Alert>
          )}

          {selectedPlatform && connectedPlatforms.includes(selectedPlatform) && (
            <Typography sx={{ ...typography.bodySmall, color: grayFontColor, mt: 1.5 }}>
              On payment success, order (items, customer, amount, reference ID) is
              created as a new order in {PLATFORM_LABELS[selectedPlatform]}. On payment
              failure or expiry, no order is pushed.
            </Typography>
          )}
        </Box>
      )}
    </Stack>
  );
};

export default OrderConfirmationPreset;
